import React, { useEffect, useMemo, useState } from 'react';
import { Icon } from './Icon';
import { fmtDateTime } from '../../utils/formatters';
import { useFecharNoEsc } from './useFecharNoEsc';
import CopiarLinkDeAssinatura, { linkDeAssinatura } from './CopiarLinkDeAssinatura';

/**
 * Modal ÚNICO de visualização de documento (o botão do olho).
 *
 * Antes disto cada tela montava o seu: Ofícios em `max-w-5xl`, Portabilidade em `max-w-6xl`,
 * Materiais em `max-w-5xl`, docs de habilitação de Licitações em `max-w-4xl` e Docs
 * Societários sem preview nenhum. Numa tela de 1999px o PDF sobrava com ~500px de largura e
 * o visualizador do Chrome abria em 61%/42%, com o painel de miniaturas comendo um terço do
 * que restava. Eram dois problemas somados: o container travado em ~1000px e o PDF aberto
 * sem nenhuma dica de zoom.
 *
 * O que este componente resolve:
 *  - largura `96vw` (limite 1800px) e altura `94vh`, então o A4 cabe em tamanho de leitura;
 *  - `#view=FitH&navpanes=0` no src, que ajusta à largura e fecha as miniaturas;
 *  - Blob URL em vez de `data:` (o base64 de um PDF assinado com anexos passa de 8 MB
 *    dentro do DOM, e o Chrome ignora navegação em `data:` acima de um limite prático);
 *  - painel lateral opcional e colapsável, com estado lembrado em localStorage;
 *  - timeline de assinatura visível SEMPRE, inclusive em documento já assinado — era o
 *    buraco que fez este componente existir: em Ofício assinado não havia nada na tela, e
 *    em Portabilidade o histórico ficava escondido atrás de "Ver Historico".
 *
 * Tela nova que abre documento usa ESTE componente. Ver docs/ui-guidelines.md §8.
 */

export interface DocViewerInfoItem {
  label: string;
  value: React.ReactNode;
  /** Cor do VALOR (a superfície nunca é pintada — guia visual §2). */
  tone?: 'default' | 'success' | 'warning' | 'danger' | 'muted';
}

export interface DocViewerSigner {
  name: string;
  email?: string;
  cpf?: string;
  role?: string;
  signedAt?: string | null;
  /** Quando vem do ClickSign ao vivo: 'signed' | 'pending'. Senão deduz de signedAt. */
  status?: string;
  /**
   * `request_signature_key` do ClickSign — é o que vira o link DESTE signatário
   * (ver `linkDeAssinatura`). Gravado por `criarDocumentoParaAssinatura` no backend,
   * então existe em Ofícios, Docs Societários, Portabilidade e Contratos Digitais.
   */
  clicksignSignerKey?: string | null;
}

// O link por signatário e o botão de copiar moram em CopiarLinkDeAssinatura (15/10/2026: o CRM usa a mesma peça).
export { linkDeAssinatura } from './CopiarLinkDeAssinatura';

export interface DocViewerEvent {
  type: string;
  label: string;
  date: string;
  /** Quem fez, quando o evento vem do statusHistory. */
  author?: string;
}

interface Props {
  title: string;
  subtitle?: string;
  /** PDF em base64, com ou sem o prefixo `data:application/pdf;base64,`. */
  pdfBase64?: string | null;
  /** URL direta (S3, blob já pronto). Tem precedência sobre `pdfBase64`. */
  fileUrl?: string | null;
  /** MIME do arquivo. Decide entre PDF, imagem, HTML e "sem pré-visualização". */
  mimeType?: string | null;
  /** Usado só para adivinhar o tipo quando não há MIME (Licitações guarda o nome, não o MIME). */
  fileName?: string | null;
  /** Sobrescreve a mensagem de formato sem visualização. */
  unsupportedMessage?: string;
  loading?: boolean;
  accent?: 'orange' | 'purple' | 'blue' | 'primary';
  info?: DocViewerInfoItem[];
  signers?: DocViewerSigner[];
  /** Data de assinatura do DOCUMENTO. Ver a nota no bloco de signatários. */
  documentSignedAt?: string | null;
  signatureUrl?: string | null;
  timeline?: DocViewerEvent[];
  timelineLoading?: boolean;
  /** Botões no cabeçalho, à esquerda do expandir/fechar (download, regerar...). */
  headerActions?: React.ReactNode;
  /** Botões dentro do painel lateral (reenviar e-mail, verificar status...). */
  sidebarActions?: React.ReactNode;
  /** Bloco livre no fim do painel. */
  sidebarExtra?: React.ReactNode;
  onClose: () => void;
}

const ACCENT_SPINNER: Record<string, string> = {
  orange: 'border-orange-500',
  purple: 'border-purple-500',
  blue: 'border-blue-500',
  // O acento do HUB (10/10/2026): as telas que saem do laranja de módulo usam este.
  primary: 'border-primary',
};

const TONE_TEXT: Record<string, string> = {
  default: 'text-white',
  success: 'text-green-400',
  warning: 'text-yellow-400',
  danger: 'text-red-400',
  muted: 'text-slate-400',
};

// Cor do ponto da timeline por tipo de evento. Nomes iguais aos que o backend devolve em
// GET /:id/history nos dois módulos (ofícios e portabilidade).
const EVENT_DOT: Record<string, string> = {
  created: 'bg-slate-400',
  emitted: 'bg-blue-400',
  sent: 'bg-amber-400',
  email_sent: 'bg-slate-400',
  email_delivered: 'bg-cyan-400',
  email_opened: 'bg-blue-400',
  email_bounced: 'bg-red-400',
  signed: 'bg-green-400',
  canceled: 'bg-red-400',
  revoked: 'bg-amber-500',
  reopened: 'bg-slate-300',
};

// Papel do signatário como o ClickSign devolve. `sign` é o caso comum e não vira linha na
// tela (dizer "Cargo: Signatário" embaixo do nome do signatário não informa nada); os outros
// dois importam, porque testemunha e aprovador NÃO são quem assina o documento. A tela antiga
// de Docs Societários já traduzia; ao migrar, "Cargo: witness" apareceu cru no painel.
const PAPEL_DO_SIGNATARIO: Record<string, string> = {
  witness: 'Testemunha',
  approve: 'Aprovador',
};

const SIDEBAR_PREF_KEY = 'docviewer:sidebar-aberto';

export const DocumentViewerModal: React.FC<Props> = ({
  title,
  subtitle,
  pdfBase64,
  fileUrl,
  mimeType,
  fileName,
  unsupportedMessage,
  loading,
  accent = 'orange',
  info,
  signers,
  documentSignedAt,
  signatureUrl,
  timeline,
  timelineLoading,
  headerActions,
  sidebarActions,
  sidebarExtra,
  onClose,
}) => {
  const temPainel = Boolean(
    (info && info.length) || (signers && signers.length) || signatureUrl || (timeline && timeline.length) || timelineLoading || sidebarActions || sidebarExtra,
  );

  const [painelAberto, setPainelAberto] = useState<boolean>(() => {
    try {
      return localStorage.getItem(SIDEBAR_PREF_KEY) !== 'false';
    } catch {
      return true;
    }
  });

  const togglePainel = () => {
    setPainelAberto((v) => {
      try {
        localStorage.setItem(SIDEBAR_PREF_KEY, String(!v));
      } catch {
        /* modo anônimo / storage bloqueado: só não lembra a preferência */
      }
      return !v;
    });
  };

  // Na PILHA das cascas (09/10/2026): com o Esc próprio, o visualizador aberto por cima de uma casca fechava os dois de
  // uma vez. Visualizador é leitura: o Esc fecha direto.
  useFecharNoEsc(onClose);

  // Blob URL: o base64 do PDF assinado com anexos mesclados passa de 8 MB, e enfiar isso
  // como `data:` no src do iframe carrega a string inteira no DOM a cada render.
  const blobUrl = useMemo(() => {
    if (fileUrl || !pdfBase64) return null;
    try {
      const puro = pdfBase64.replace(/^data:application\/pdf;base64,/, '');
      const bytes = Uint8Array.from(atob(puro), (c) => c.charCodeAt(0));
      return URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));
    } catch (e) {
      console.error('[DocumentViewerModal] base64 inválido:', e);
      return null;
    }
  }, [pdfBase64, fileUrl]);

  useEffect(() => {
    // Sem o revoke o blob fica na memória até o fim da sessão, e o modal reabre a cada olho.
    return () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [blobUrl]);

  // Que tipo de conteúdo é. Materiais guardam MIME (PDF, HTML, imagem, vídeo); Licitações
  // guarda só o nome do arquivo. Com base64 presente é PDF por construção.
  const tipo = useMemo((): 'pdf' | 'imagem' | 'html' | 'outro' => {
    const mime = (mimeType || '').toLowerCase();
    const nome = (fileName || '').toLowerCase();
    if (mime === 'application/pdf' || nome.endsWith('.pdf')) return 'pdf';
    if (mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|svg|bmp)$/.test(nome)) return 'imagem';
    if (mime === 'text/html' || nome.endsWith('.html') || nome.endsWith('.htm')) return 'html';
    if (!mime && !nome) return 'pdf'; // chamador que só passa base64/URL de PDF
    return 'outro';
  }, [mimeType, fileName]);

  const url = fileUrl || blobUrl;

  // FitH ajusta à LARGURA (o A4 abria em 61% porque ninguém dizia nada ao viewer) e
  // navpanes=0 fecha o painel de miniaturas, que o Chrome reabre por lembrar do último uso.
  // Só vale para PDF: em imagem e HTML o fragmento vira lixo na URL (e URL assinada do S3
  // com querystring já traz `?`, por isso o separador é calculado).
  const src = useMemo(() => {
    if (!url) return null;
    if (tipo !== 'pdf') return url;
    return url + (url.includes('#') ? '&' : '#') + 'view=FitH&navpanes=0&toolbar=1&pagemode=none';
  }, [url, tipo]);

  const eventos = timeline || [];

  return (
    <div
      className='fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-3'
      onMouseDown={(e) => {
        // mouseDown e não click: soltar o botão em cima do backdrop depois de selecionar
        // texto DENTRO do PDF fechava o modal no meio da leitura.
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className='bg-slate-800 rounded-xl shadow-2xl border border-slate-700 flex flex-col w-[96vw] max-w-[1800px] h-[94vh] overflow-hidden'>
        {/* Cabeçalho */}
        <div className='flex items-center justify-between gap-4 px-4 py-3 border-b border-slate-700 shrink-0'>
          <div className='min-w-0'>
            <h3 className='text-base font-semibold text-white truncate' title={title}>{title}</h3>
            {subtitle && (
              <p className='text-sm text-slate-400 truncate' title={subtitle}>{subtitle}</p>
            )}
          </div>
          <div className='flex items-center gap-2 shrink-0'>
            {headerActions}
            {src && (
              <a
                href={src}
                target='_blank'
                rel='noopener noreferrer'
                className='p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg'
                title='Abrir em nova aba'
              >
                <Icon name='externalLink' className='w-5 h-5' />
              </a>
            )}
            {temPainel && (
              <button
                onClick={togglePainel}
                className='p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg'
                title={painelAberto ? 'Ocultar painel (documento em tela cheia)' : 'Mostrar painel de informações'}
              >
                <Icon name={painelAberto ? 'panelClose' : 'panelOpen'} className='w-5 h-5' />
              </button>
            )}
            <button onClick={onClose} className='p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg' title='Fechar (Esc)'>
              <Icon name='x' className='w-5 h-5' />
            </button>
          </div>
        </div>

        {/* Corpo */}
        <div className='flex-1 flex overflow-hidden'>
          <div className='flex-1 min-w-0 p-3'>
            {loading ? (
              <div className='flex items-center justify-center h-full'>
                <div className={`animate-spin rounded-full h-12 w-12 border-b-2 ${ACCENT_SPINNER[accent]}`} />
              </div>
            ) : unsupportedMessage || (src && tipo === 'outro') ? (
              <div className='flex flex-col items-center justify-center h-full text-center gap-2'>
                <Icon name='document' className='w-14 h-14 text-slate-600' />
                <p className='text-sm text-slate-400'>
                  {unsupportedMessage || 'Pré-visualização não disponível para este formato.'}
                </p>
                {fileName && <p className='text-xs text-slate-500'>{fileName}</p>}
              </div>
            ) : src && tipo === 'imagem' ? (
              <div className='flex items-center justify-center h-full'>
                <img src={src} alt={title} className='max-w-full max-h-full object-contain rounded-lg' />
              </div>
            ) : src ? (
              <iframe
                src={src}
                // Fundo branco no HTML: material em HTML é feito para papel/tela clara e
                // some no slate-900. Em PDF o próprio viewer pinta o fundo.
                className={`w-full h-full border border-slate-600 rounded-lg ${tipo === 'html' ? 'bg-white' : 'bg-slate-900'}`}
                title='Visualizar documento'
              />
            ) : (
              <div className='flex flex-col items-center justify-center h-full text-center gap-2'>
                <Icon name='document' className='w-14 h-14 text-slate-600' />
                <p className='text-sm text-slate-400'>Não foi possível carregar o documento.</p>
              </div>
            )}
          </div>

          {temPainel && painelAberto && (
            <aside className='w-[360px] shrink-0 border-l border-slate-700 overflow-y-auto p-4 space-y-5'>
              {info && info.length > 0 && (
                <div>
                  <h4 className='text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2'>Informações</h4>
                  <div className='space-y-2 text-sm'>
                    {info.map((item, i) => (
                      <div key={i} className='flex justify-between gap-3'>
                        <span className='text-slate-400 shrink-0'>{item.label}</span>
                        <span className={`text-right min-w-0 truncate ${TONE_TEXT[item.tone || 'default']}`} title={typeof item.value === 'string' ? item.value : undefined}>
                          {item.value}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {sidebarActions && <div className='flex gap-2'>{sidebarActions}</div>}

              {signers && signers.length > 0 && (
                <div>
                  <h4 className='text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2'>Signatários</h4>
                  <div className='space-y-3'>
                    {signers.map((s, i) => {
                      // Documento assinado cujo signatário está sem data própria: até
                      // 24/08/2026 o webhook do ofício gravava `signedAt` no documento e não
                      // carimbava cada signatário, então o OF-2026/030 aparecia ASSINADO com
                      // o signatário "Pendente". O backend passou a carimbar, mas os
                      // documentos assinados ANTES disso não têm como recuperar a data
                      // individual — aqui eles aparecem como assinados, sem inventar hora.
                      const assinou = s.status
                        ? s.status === 'signed'
                        : Boolean(s.signedAt) || Boolean(documentSignedAt);
                      const semDataPropria = assinou && !s.signedAt;
                      return (
                        <div key={i} className='bg-slate-900/50 rounded-lg p-3 border border-slate-700/50'>
                          <div className='flex items-center gap-2 mb-1'>
                            <span className={`w-2 h-2 rounded-full shrink-0 ${assinou ? 'bg-green-400' : 'bg-yellow-400'}`} />
                            <span className='text-white text-sm font-medium'>{s.name}</span>
                          </div>
                          <div className='pl-4 space-y-0.5 text-xs'>
                            {s.email && <div className='text-slate-400 break-all'>{s.email}</div>}
                            {s.cpf && <div className='text-slate-500'>CPF: {s.cpf}</div>}
                            {s.role && s.role !== 'sign' && (
                              <div className='text-slate-500'>{PAPEL_DO_SIGNATARIO[s.role] || s.role}</div>
                            )}
                            <div
                              className={assinou ? 'text-green-400' : 'text-yellow-400'}
                              title={semDataPropria ? 'Documento assinado em ' + fmtDateTime(documentSignedAt) + '. A data por signatário só passou a ser registrada em 24/08/2026.' : undefined}
                            >
                              {!assinou ? 'Pendente' : s.signedAt ? 'Assinou em ' + fmtDateTime(s.signedAt) : 'Assinou'}
                            </div>
                          </div>
                          {/* Só para quem ainda não assinou: link assinado não serve para nada. */}
                          {!assinou && <CopiarLinkDeAssinatura signer={s} className='mt-2 ml-4' />}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/*
                Só quando NENHUM signatário tem chave própria (documento antigo, anterior à
                gravação do `clicksignSignerKey`). Tendo chave, o link por signatário acima é
                o certo: este aqui é sempre o do PRIMEIRO, e mostrá-lo ao lado dos outros
                convida a mandar para a pessoa errada.
              */}
              {signatureUrl && !(signers || []).some((s) => linkDeAssinatura(s)) && (
                <div>
                  <h4 className='text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2'>Link de assinatura</h4>
                  <a href={signatureUrl} target='_blank' rel='noopener noreferrer' className='text-xs text-purple-400 hover:text-purple-300 break-all'>
                    {signatureUrl}
                  </a>
                </div>
              )}

              {(eventos.length > 0 || timelineLoading) && (
                <div>
                  <h4 className='text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2 flex items-center gap-1.5'>
                    <Icon name='clock' className='w-3.5 h-3.5' /> Histórico
                  </h4>
                  {timelineLoading && eventos.length === 0 ? (
                    <p className='text-xs text-slate-500'>Carregando histórico...</p>
                  ) : (
                    <div className='space-y-2 border-l-2 border-slate-600 pl-3 ml-1'>
                      {eventos.map((evt, i) => (
                        <div key={i} className='relative'>
                          <span className={`absolute -left-[17px] top-1 w-2 h-2 rounded-full ${EVENT_DOT[evt.type] || 'bg-slate-400'}`} />
                          <div className='text-[10px] text-slate-500'>{fmtDateTime(evt.date)}</div>
                          <div className='text-xs text-slate-300'>{evt.label}</div>
                          {evt.author && <div className='text-[10px] text-slate-500'>por {evt.author}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {sidebarExtra}
            </aside>
          )}
        </div>
      </div>
    </div>
  );
};

export default DocumentViewerModal;
